/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {ApiWithQuery, HttpMethod} from "@intuitionrobotics/thunderstorm";
import {ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {dataURItoBlob} from "@modules/utils";
import {DefaultResponses, FileKey_MessagingProxy, Response_ContactMe} from "@app/app-shared/message";
import {Api_CcGetReadSignedUrl} from "@app/app-shared/api";
import {batchAction, currentTimeMillies, Module, PartialProperties, ThisShouldNotHappenException} from "@intuitionrobotics/ts-common";
import {Body_ByProxy_SendMedia, Body_SendText, BucketPointerData, DB_Message, MediaInfo, MediaTypes, MessageMap, MessageStatuses, VideoInfo} from "@app/ir-q-app-common/types/message";
import {
    Api_MessageGetLastByProxy,
    Api_MessageSetAsRead,
    Api_MessageSetAsUnread,
    Api_OnBehalf_SendMedia,
    Api_OnBehalf_SendText,
    Api_ScheduledMessages_Query,
    Api_ScheduledMessages_Update,
    Api_ScheduledMessages_Delete,
    CCInbox,
    MessageWithStatus,
    Response_CCGetInbox
} from "@app/ir-q-app-common/types/message-api";
import {
    ScheduledMessageInfo,
    Body_QueryScheduledMessages,
    Body_UpdateScheduledMessage,
    Body_DeleteScheduledMessage
} from "@app/ir-q-app-common/types/scheduled-messages";
import dayjs from "../app/utils/dayjs";
import {generateHex} from "@intuitionrobotics/ts-common/utils/random-tools";
import {UsersModule} from "@modules/UsersModule";
import {UploaderModule} from "@intuitionrobotics/file-upload/frontend";
import {FileStatus} from "@intuitionrobotics/file-upload/shared/modules/BaseUploaderModule";
import {DB_Temp_File} from "@intuitionrobotics/file-upload/shared/types";
import {DB_Contact, Relation} from "@app/ir-q-app-common/types/db-contact";
import {ChatsContextType} from "../app/pages/ui/messages/ChatsContext";
import {dispatch_onProgress} from "../app/pages/widget/ProgressBar";
import {ContactsMap} from "../app/pages/ui/messages/ContactsContext";

export const RequestKey_SendMessage = "send-message";
export const RequestKey_GetSignedUrl = "get-read-signed-url";
export const RequestKey_SetAsRead = "set-as-read";
export const RequestKey_SetAsUnread = "set-as-unread"

export type Api_GetGroupChatsByProxy = ApiWithQuery<"/v1/inbox/by-proxy/get-group-inbox", Response_CCGetInbox>
export const FileKey_Base64 = "file-base64";
export type UploadingDetails = {
    needsResponse: boolean
    senderGroupContactIds: string[],
    recipientContactIds: string[]
    file: File,
    attachedText?: string
    thumbnailFeId?: string
    scheduledSend?: number
};

const getContactName = (c: DB_Contact<any>) => {
    return `${c._id} -> ${c.firstName} ${c.middleName || ""} ${c.lastName}`;
}

export class MessagingModule_Class
    extends Module {

    private filesUploadingDetails: {
        [feId: string]: UploadingDetails
    } = {};
    private mediaCache: Map<string, File> = new Map();

    private thumbnailFiles: { [key: string]: { file: File, bucketPointer?: BucketPointerData } } = {}

    // private openLastMessage?: MessageInfo_GetLastMessage;

    constructor() {
        super("MessagingModule");
    }

    queryChats = async (contacts: ContactsMap, groupIds: string[], saveChats: (_c: CCInbox[]) => void): Promise<(CCInbox[] | undefined)[]> => {
        return batchAction(groupIds, 6, async (_ids, chunkNumber: number) => {
            return Promise.all(_ids.map(async (_id, index) => {
                const queriedChats = await this.queryChat(contacts, _id, undefined, ((index + 1) + (chunkNumber * 6)) / (groupIds?.length || index))
                queriedChats && saveChats(queriedChats);
                return queriedChats;
            }))
        });
    }

    queryChat: (contacts: ContactsMap, groupContactId?: string, chatId?: string, ratio?: number) => Promise<CCInbox[] | undefined> = async (contacts: ContactsMap, groupContactId?: string, chatId?: string, ratio?: number) => {
        const onError = (_contacts: ContactsMap, debugMessage?: string) => {
            this.logWarning("Got error message", debugMessage);

            if (groupContactId) {
                const groupContact = _contacts[groupContactId];

                if (groupContact)
                    ToastModule.toastError(
                        `Query to fetch chats for organization ${groupContact.firstName} ${groupContact.lastName || ""} ${groupContact.middleName || ""} failed!. Refresh to page to avoid partial data`);
            }

            if (ratio)
                dispatch_onProgress.dispatchUI("chats", ratio);
        }

        try {
            const response = await XhrHttpModule
                .createRequest<Api_GetGroupChatsByProxy>(HttpMethod.GET, "get-group-inbox")
                .setRelativeUrl("/v1/inbox/by-proxy/get-group-inbox")
                .setUrlParams({groupContactId, chatId})
                .setOnError((_, resError) => onError(contacts, resError?.debugMessage))
                .executeSync();

            if (ratio)
                dispatch_onProgress.dispatchUI("chats", ratio);

            if (!Object.keys(contacts).length)
                return response.chats;

            return this.filterNonConnectedChats(response.chats, contacts);
        } catch (e) {
            onError(contacts, e && e instanceof Error ? e.message : JSON.stringify(e));
        }
    };


    public filterNonConnectedChats(chats: CCInbox[], contacts: ContactsMap) {
        return chats.filter(c => {
            if (c.contactIds.length < 2)
                return false;

            // Keep the chat if any two of its participants are connected - a chat can have more than two.
            const areConnected = c.contactIds.some(first => c.contactIds.some(second => first !== second && contacts[first]?.connections.some(connection => connection._id === second)));

            if (!areConnected) {
                const [f, s] = c.contactIds;
                const firstContact = contacts[f];
                const secondContact = contacts[s];
                this.logWarning(`Contacts are not connected: ${firstContact ? getContactName(firstContact) : f} to ${secondContact ? getContactName(secondContact) : s}`);
            }

            return areConnected
        });
    }

    send = async (text: string, senderId: string, contactIds: string[], needsResponse = false, scheduledSend?: number) => {
        const messagesResponses = this.getMessageResponses(needsResponse);
        const body: Body_SendText = {
            contactIds,
            text,
            senderId
        };
        if (messagesResponses)
            body.choices = {options: messagesResponses};
        if (scheduledSend)
            body.scheduledSend = scheduledSend;

        return XhrHttpModule
            .createRequest<Api_OnBehalf_SendText>(HttpMethod.POST, RequestKey_SendMessage)
            .setJsonBody(body)
            .setRelativeUrl("/v1/message/on-behalf/send-text")
            .setOnError((_, resError) => {
                this.logWarning("Got error message", resError?.debugMessage);
            })
            .executeSync();
    };

    onSentMessageInExistingChat(foundChat: CCInbox, senderId: string, data: MessageMap[keyof MessageMap], messageType: string, file?: File) {
        const message = {
            _id: generateHex(32),
            chatId: foundChat._id,
            type: messageType,
            senderId: senderId,
            metadata: {ghostUserId: UsersModule.getCurrentUserByEmail()?._id || ""},
            data: data,
            draft: true,
            timestamp: dayjs().valueOf()
        };

        if (messageType !== "text" && file)
            this.mediaCache.set(this.getStorageMediaKey(message._id), file);

        return message;
    }

    private getMessageResponses(needsResponse: boolean): DB_Message<"text">[] | undefined {
        if (!needsResponse)
            return;

        return DefaultResponses.map((response: string) => {
            return {
                _id: generateHex(32),
                chatId: "",
                type: "text",
                senderId: "",
                data: {text: response},
                draft: true,
                needsFollowup: response === Response_ContactMe,
                timestamp: dayjs().valueOf()
            };
        });
    }

    public getChatHash = (contactId: string, senderId: string) => `${contactId}-${senderId}`;

    generateVideoThumbnail = (file: File): Promise<string> => {
        return new Promise((resolve) => {
            const canvas = document.createElement("canvas");
            const video = document.createElement("video");

            // this is important
            video.autoplay = true;
            video.muted = true;
            video.src = URL.createObjectURL(file);

            video.onloadeddata = () => {
                const ctx = canvas.getContext("2d");

                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;

                ctx?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
                video.pause();
                return resolve(canvas.toDataURL("image/png"));
            };
        });
    };

    private filterDuplicateFiles = (files: File[]) => {
        const seen = new Set<string>();
        return files.filter(file => {
            const key = `${file.name}-${file.size}-${file.lastModified}-${file.type}`;
            if (seen.has(key)) {
                this.logWarning(`Dropping duplicate file from send: ${file.name}`);
                return false;
            }

            seen.add(key);
            return true;
        });
    };

    async sendMediaMessageFromUI(senderGroupContactIds: string[], recipientContactIds: string[], dataToSend: {
                                     text?: string;
                                     files: File[]
                                 },
                                 needsResponse: boolean,
                                 onFileStatusChanged: (feId: string, status: FileStatus) => void,
                                 thumbnailFile?: File,
                                 scheduledSend?: number) {

        // One upload per file means one message on the unit, so a duplicate entry in the
        // list is a duplicate message the user receives. Never send the same file twice
        // within one send, whatever the caller handed us.
        const files = this.filterDuplicateFiles(dataToSend.files);

        const filesAndDetails = await Promise.all(files.map(async file => {

            const uploadingDetails: UploadingDetails = {
                file,
                senderGroupContactIds,
                recipientContactIds,
                needsResponse,
                scheduledSend
            };
            if (file.type.includes("video")) {
                const thumbnailUploaded = await this.generateThumbnail(file, thumbnailFile);
                uploadingDetails.thumbnailFeId = thumbnailUploaded.feId
            }

            // We dont yet handle text and audio in elliq ui
            if (!file.type.includes("audio")) {
                const attachedText = files.length === 1 ? dataToSend.text : undefined;
                if (attachedText)
                    uploadingDetails.attachedText = attachedText;
            }

            return uploadingDetails
        }));

        UploaderModule
            .upload(filesAndDetails.map(o => o.file), onFileStatusChanged, FileKey_MessagingProxy)
            .map((f, idx) => this.filesUploadingDetails[f.feId] = filesAndDetails[idx]);

    }

    async sendTextMessageFromUI(contacts: ContactsMap, senderGroupContactIds: string[], recipientContactIds: string[], text: string,
                                needsResponse: boolean, scheduledSend?: number): Promise<DB_Message<any>[][]> {

        return Promise.all(senderGroupContactIds.reduce((carry: Promise<DB_Message<any>[]>[], senderId) => {
            const groupContact = contacts[senderId];

            if (!groupContact)
                throw new ThisShouldNotHappenException(`Attempt to send message without a groupContact existing with id ${senderId}. check if it was deleted`)

            const recipients = recipientContactIds.filter(
                contactId => groupContact.connections.find(connection => connection._id === contactId && connection.relation === Relation.AGENT_USER));
            if (recipients.length > 0) {
                carry.push(this.send(text, senderId, recipients, needsResponse, scheduledSend))
            }

            return carry;
        }, []));
    }

    private async generateThumbnail(file: File, customThumbnailFile?: File) {
        const thumbnailFile = customThumbnailFile || new File([dataURItoBlob(await this.generateVideoThumbnail(file))], generateHex(8), {type: "image/png"});

        const [thumbnailUploaded] = UploaderModule.upload([thumbnailFile], this.onFileStatusChanged, FileKey_MessagingProxy);
        this.thumbnailFiles[thumbnailUploaded.feId] = {file: thumbnailFile};

        return thumbnailUploaded;
    }

    public onFileStatusChanged = (feId: string, status: FileStatus) => {
        if (!feId)
            return {};

        const uploadingDetails = this.filesUploadingDetails[feId];
        const thumbnailFile = this.thumbnailFiles[feId];

        if (!uploadingDetails && !thumbnailFile)
            return {};

        const fileInfo = UploaderModule.getFullFileInfo(feId);
        if (!fileInfo)
            throw new ThisShouldNotHappenException("I should always have a fileInfo");

        if (status !== FileStatus.PostProcessing)
            return {};

        if (!fileInfo.tempDoc)
            throw new ThisShouldNotHappenException("I should always have a temp doc if I am in post processing");

        if (thumbnailFile) {
            this.thumbnailFiles[feId] = {...this.thumbnailFiles[feId], bucketPointer: {bucketName: fileInfo.tempDoc.bucketName, path: fileInfo.tempDoc.path}}
            return {};
        }

        return {uploadingDetails, doc: fileInfo.tempDoc}
    };

    public async sendMediaOnBehalf(contacts: ContactsMap, uploadingDetails: UploadingDetails, tempDoc: DB_Temp_File, cCtx: ChatsContextType,
                                   onSendError: (data: MediaInfo, mimeType: string, messages: DB_Message<MediaTypes>[], file: File) => void,
                                   onSendSuccess: (data: MediaInfo, mimeType: string, messages: DB_Message<MediaTypes>[], file: File) => void
    ) {
        if (uploadingDetails.recipientContactIds.length > 1)
            ToastModule.toastInfo(`Started sending messages to ${uploadingDetails.recipientContactIds.length} customers`)

        let sentContactIds = 0;

        for (const senderId of uploadingDetails.senderGroupContactIds) {
            const groupContact = contacts[senderId];
            if (!groupContact)
                throw new ThisShouldNotHappenException(`Attempt to send message without a groupContact existing with id ${senderId}. check if it was deleted`)

            const contactIds = uploadingDetails.recipientContactIds.filter(
                contactId => groupContact.connections.find(connection => connection._id === contactId && connection.relation === Relation.AGENT_USER));

            const needsResponse = uploadingDetails.needsResponse;
            const attachedText = uploadingDetails.attachedText;
            const messagesResponses = this.getMessageResponses(needsResponse);

            const mediaInfo: PartialProperties<MediaInfo, "cacheId"> = {
                mimeType: tempDoc.mimeType,
                bucketPointer: {bucketName: tempDoc.bucketName, path: tempDoc.path},
                attachedText
            };

            if (uploadingDetails.thumbnailFeId) {
                const thumbnailFile = this.thumbnailFiles[uploadingDetails.thumbnailFeId];
                if (thumbnailFile && thumbnailFile.bucketPointer) {
                    (mediaInfo as MediaInfo & VideoInfo).thumbnailData = {
                        bucketPointer: thumbnailFile.bucketPointer,
                        mimeType: "image/png"
                    }
                }
            }

            if (messagesResponses)
                mediaInfo.choices = {options: messagesResponses};

            await batchAction(contactIds, 20, async (_ids) => {
                const body: Body_ByProxy_SendMedia = {
                    contactIds: _ids,
                    mediaInfo,
                    senderId
                };
                if (uploadingDetails.scheduledSend)
                    body.scheduledSend = uploadingDetails.scheduledSend;

                // both the request's onError handler and the catch below used to report the
                // same failure, so every failed send raised two error toasts
                let errorReported = false;
                const reportError = () => {
                    if (errorReported)
                        return;

                    errorReported = true;
                    onSendError(mediaInfo, tempDoc.mimeType.split("/")[0], [], uploadingDetails.file);
                };

                const request = XhrHttpModule
                    .createRequest<Api_OnBehalf_SendMedia>(HttpMethod.POST, RequestKey_SendMessage)
                    .setJsonBody(body)
                    .setRelativeUrl("/v1/message/on-behalf/send-media")
                    .setOnError(reportError);

                try {
                    const sentMessages = await request.executeSync();

                    onSendSuccess(mediaInfo, tempDoc.mimeType.split("/")[0], sentMessages, uploadingDetails.file);
                    sentContactIds += _ids.length;

                    if (uploadingDetails.recipientContactIds.length > 1)
                        ToastModule.toastInfo(`Sent messages to ${sentContactIds} customers out of ${uploadingDetails.recipientContactIds.length}`)

                } catch (e) {
                    reportError();
                }
            })
        }

        await this.queryChats(contacts, uploadingDetails.senderGroupContactIds, cCtx.saveChats).then(queriedChats => {
            const failedUnits: string[] = queriedChats.reduce((carry: string[], elem) => {
                if (!elem)
                    return carry;

                for (const c of elem) {
                    if (c.lastMessage.status.status !== MessageStatuses.FAILED)
                        continue

                    const senderId = c.contactIds.find(_id => uploadingDetails.senderGroupContactIds.includes(_id));
                    const unitContactId = c.contactIds.find(_id => _id !== senderId && uploadingDetails.recipientContactIds.includes(_id));
                    if (!unitContactId)
                        return carry;

                    const unitContact = contacts[unitContactId];
                    const unitId = unitContact?.contactData.unitId;
                    if (unitId)
                        carry.push(unitId)
                }

                return carry;
            }, []);

            if (failedUnits.length) {
                const errorMessage = `Failed to send media message to following units: ${failedUnits.join()}`;
                ToastModule.toastError(errorMessage);
                this.logError(errorMessage)
            }
        }).catch(this.logError)

    }

    private getStorageMediaKey = (messageId: string) => {
        return `${FileKey_Base64}_${messageId}`;
    };

    getMessageMedia(messageId: string) {
        return this.mediaCache.get(this.getStorageMediaKey(messageId));
    }

    getNumberOfUnreadMessages(chat: CCInbox): number {
        return chat.unreadCount || 0;
    }

    isResponse = (contacts: ContactsMap, chat: CCInbox) => {
        const relevantContacts = chat.contactIds.map(cId => contacts[cId]);
        return (!!chat.lastMessage.replyId ||
            !!relevantContacts.find(contact => contact._id === chat.lastMessage.senderId && contact.contactType === "agentUser"));
    }

    async queryMessages(chatId: string, groupContactId: string, count = 100, fromTimestamp?: number) {
        return XhrHttpModule
            .createRequest<Api_MessageGetLastByProxy>(HttpMethod.POST, "read-chat")
            .setJsonBody({chatId: chatId, senderId: groupContactId, count, timestamp: fromTimestamp})
            .setRelativeUrl("/v1/message/by-proxy/get-lasts")
            .setOnError((_, resError) => this.logWarning("Error reading chat id", chatId, resError))
            .executeSync();
    };

    public needsToFetchSignedUrl(message: MessageWithStatus) {
        return message.type !== "text" && ((message.data.ttl && message.data.ttl < currentTimeMillies()) || (!message.data.signedUrl && !this.getMessageMedia(message._id)));
    }

    setAsRead(messageId: string, recipientId: string) {
        return XhrHttpModule
            .createRequest<Api_MessageSetAsRead>(HttpMethod.POST, RequestKey_SetAsRead)
            .setJsonBody({messageId, recipientId})
            .setRelativeUrl("/v1/message/status/set-as-read")
            .setOnError(() => this.logWarning(`Error setting as read message with id ${messageId}`))
            .executeSync();
    }

    setAsUnread(chat: CCInbox, recipientId: string) {
        return XhrHttpModule
            .createRequest<Api_MessageSetAsUnread>(HttpMethod.POST, RequestKey_SetAsUnread)
            .setJsonBody({chatId: chat._id, senderId: recipientId})
            .setRelativeUrl("/v1/message/status/set-as-unread")
            .setOnError(() => this.logWarning(`Error as with try to mark unread message with chatId ${chat._id}`))
            .executeSync();
    }

    // chatId + senderId (the chat's groupContactId) are what authorize this call backend-side —
    // customGroupContactMiddleWare gates on senderId and the handler asserts the message really
    // belongs to that chat. Without them the route is a 400.
    getReadSignedUrl(messageId: string, chatId: string, groupContactId: string) {
        return XhrHttpModule
            .createRequest<Api_CcGetReadSignedUrl>(HttpMethod.POST, RequestKey_GetSignedUrl)
            .setJsonBody({messageId, chatId, senderId: groupContactId})
            .setRelativeUrl("/v1/message/get-read-signed-url")
            .setOnError(() => this.logWarning(`Error getting the signed url for message with id ${messageId}}`))
            .executeSync();
    }

    queryScheduledMessages(contactIds?: string[]): Promise<ScheduledMessageInfo[]> {
        const body: Body_QueryScheduledMessages = {};
        if (contactIds)
            body.contactIds = contactIds;

        return XhrHttpModule
            .createRequest<Api_ScheduledMessages_Query>(HttpMethod.POST, "query-scheduled-messages")
            .setJsonBody(body)
            .setRelativeUrl("/v1/message/scheduled/query")
            .setOnError((_, resError) => this.logWarning("Error querying scheduled messages", resError?.debugMessage))
            .executeSync();
    }

    updateScheduledMessage(taskId: string, sendBody?: Body_SendText | Body_ByProxy_SendMedia, executionTimestamp?: number) {
        const body: Body_UpdateScheduledMessage = {taskId};
        if (sendBody)
            body.sendBody = sendBody;
        if (executionTimestamp)
            body.executionTimestamp = executionTimestamp;

        return XhrHttpModule
            .createRequest<Api_ScheduledMessages_Update>(HttpMethod.POST, "update-scheduled-message")
            .setJsonBody(body)
            .setRelativeUrl("/v1/message/scheduled/update")
            .setOnError((_, resError) => this.logWarning("Error updating scheduled message", resError?.debugMessage))
            .executeSync();
    }

    deleteScheduledMessage(taskId: string) {
        const body: Body_DeleteScheduledMessage = {taskId};

        return XhrHttpModule
            .createRequest<Api_ScheduledMessages_Delete>(HttpMethod.POST, "delete-scheduled-message")
            .setJsonBody(body)
            .setRelativeUrl("/v1/message/scheduled/delete")
            .setOnError((_, resError) => this.logWarning("Error deleting scheduled message", resError?.debugMessage))
            .executeSync();
    }
}

export const MessagingModule = new MessagingModule_Class();
